// Zustand Thunk
import debounce from '../../utils/debounce'
import {
  ActionCardData,
  ActionCardModel,
  AssessmentData,
  Element,
  ElementKind,
  getActiveTagNames,
  getFlowStatus,
  QuestionData,
} from '@playbooks/action-cards-utils/src'

import { Get, Set } from '..'
import getDifference from '../../utils/getDifference'
import getQuestionsWithCode from '../../utils/getQuestionsWithCode'
import handleActionCards from '../../utils/handleActionCards'

export type AnyUpdateTuple = [target: object, key: PropertyKey, value: unknown]
type AssessmentReset = { assessmentReset: boolean; assessment: AssessmentData }

export type ActionUpdate = {
  <T, K extends keyof T>(target: T, key: K, value: T[K]): Promise<void>
  (targetOrUpdates: { assessmentReset: true; assessment: AssessmentData }): Promise<void>
  (targetOrUpdates: AnyUpdateTuple[]): Promise<void>
  (): Promise<void>
}

type UpdateThunkProps = {
  prevActionCardsDataRef: React.RefObject<ActionCardData | null>
}

const debounced = debounce()

function actionUpdate(set: Set, get: Get, { prevActionCardsDataRef }: UpdateThunkProps) {
  async function update<T, K extends keyof T>(target: T, key: K, value: T[K]): Promise<void>
  async function update(targetOrUpdates: AssessmentReset): Promise<void>
  async function update(targetOrUpdates: AnyUpdateTuple[]): Promise<void>
  async function update(): Promise<void>

  async function update<T, K extends keyof T>(
    targetOrUpdates?: T | AnyUpdateTuple[] | AssessmentReset,
    key?: K,
    value?: T[K],
  ): Promise<void> {
    const { data, flowData, actionCardsModel } = get()

    switch (true) {
      case targetOrUpdates === undefined:
        // no-op update: just recalculate and notify
        break
      case Array.isArray(targetOrUpdates):
        for (const [target, k, v] of targetOrUpdates) {
          ;(target as Record<PropertyKey, unknown>)[k] = v
        }
        break
      case (targetOrUpdates as AssessmentReset).assessmentReset:
        const assessment = new ActionCardModel(data).getSearchService().find(node => {
          const assessment = node.data as AssessmentData
          return assessment.id === (targetOrUpdates as AssessmentReset).assessment.id
        }).data as AssessmentData

        assessment.checked = assessment.isAutoCollapse ? false : true
        assessment.responses.forEach(response => (response.checked = false))
        assessment.freeTextValue = ''
        break
      default:
        ;(targetOrUpdates as Record<PropertyKey, unknown>)[key as PropertyKey] = value
    }

    const clonedData = JSON.parse(JSON.stringify(data)) as ActionCardData
    const newActionCardModel = new ActionCardModel(clonedData)

    handleActionCards(clonedData)

    const currentFlowData = getFlowStatus(clonedData)

    clonedData.actionCardFlow = currentFlowData.flow

    const questionsWithCode = getQuestionsWithCode(newActionCardModel)

    questionsWithCode.forEach(field => {
      if ((targetOrUpdates as Element)?.kind !== ElementKind.QUESTION) return
      const question = targetOrUpdates as QuestionData

      if (!!field.code && field.type === question.type && field.code === question.code && field.id !== question.id) {
        // We need this for the Dropdown question type to prevent
        // not existed answer assignment to another dropdown
        if ((question.data as [])?.length !== (field.data as [])?.length) return

        field.value = question.value
      }
    })

    set(state => ({
      ...state,
      data: clonedData,
      actionCardsModel,
      flowData: currentFlowData,
      questionsWithCode,
    }))

    debounced(() => {
      if (currentFlowData.flow !== flowData.flow) {
        get().onFlowChange?.(currentFlowData.flow)
      }

      const diff = getDifference(clonedData, prevActionCardsDataRef.current!)
      const tags = getActiveTagNames(clonedData) || undefined

      prevActionCardsDataRef.current = JSON.parse(JSON.stringify(clonedData)) as ActionCardData

      get().onChange?.(clonedData, diff, tags)
    })
  }
  return update
}

export default actionUpdate
